/**
 * OIDC client configuration for react-oidc-context.
 *
 * Reads VITE_OIDC_AUTHORITY + VITE_OIDC_CLIENT_ID injected at Vite
 * build time. In the AWS monolith deploy, the compose web.build.args
 * pulls these from .env (operator-pasted from `docker compose logs
 * zitadel-init` after the seed phase mints the OIDC client).
 *
 * When either env var is empty, isOidcConfigured() returns false and
 * the AuthProvider degrades to "no-auth shell" rather than crash. This
 * matches the AUTH_DEV_MODE=true compose path on the backend and is
 * what gets used in local-dev when the operator hasn't wired Zitadel.
 *
 * See:
 *  - infra/local/zitadel/zitadel-seed.sh — writes the client_id
 *  - platform-core/deploy/docker-compose.aws.yml — web.build.args
 *  - docs/decisions/2026-05-29_zitadel-v2-login-pat.md — the bigger picture
 */

import type { UserManagerSettings } from "oidc-client-ts";
import { Log, UserManager, WebStorageStateStore } from "oidc-client-ts";
import { hasAuthParams } from "react-oidc-context";

import { createBrowserTabSession, type TabSession } from "./tab-session";

// Turn on verbose oidc-client-ts logging so PKCE failures, discovery
// errors, and silent-renew issues all surface in the browser console.
// The library's default is OFF (no logs at all), which makes "stuck on
// Redirecting…" hard to diagnose. We can dial back to WARN once the
// real-auth path is bedded down.
if (typeof window !== "undefined") {
  Log.setLogger(console);
  Log.setLevel(Log.INFO);
}

const authority = import.meta.env.VITE_OIDC_AUTHORITY ?? "";
const clientId = import.meta.env.VITE_OIDC_CLIENT_ID ?? "";

export function isOidcConfigured(): boolean {
  return authority !== "" && clientId !== "";
}

/**
 * Built lazily at module load so window.location is available.
 * Vite SSRs nothing — this module only ever runs in the browser — but
 * being defensive costs nothing.
 */
function buildOidcConfig(): UserManagerSettings {
  const origin =
    typeof window !== "undefined" ? window.location.origin : "http://localhost:5173";
  return oidcSettings(authority, clientId, origin, window.sessionStorage);
}

/**
 * The app's OIDC settings for an authority, a client, the app's origin and
 * the tab's storage. The tests build their UserManagers with it too
 * (tab-session.oidc.test.ts).
 */
export function oidcSettings(
  authority: string,
  clientId: string,
  origin: string,
  tabStorage: Storage,
): UserManagerSettings {
  return {
    authority,
    client_id: clientId,
    redirect_uri: `${origin}/auth/callback`,
    post_logout_redirect_uri: `${origin}/`,
    response_type: "code",
    // openid+email+profile gives sub, email, name (claims our User type
    // maps onto). offline_access is what unlocks silent refresh via
    // refresh_token (Zitadel honours it when the client opts in).
    scope: "openid profile email offline_access",
    // Pull userinfo so claims missing from id_token (e.g. name on some
    // Zitadel setups) get populated from /oidc/v1/userinfo.
    loadUserInfo: true,
    // Use sessionStorage so a tab close ends the session; localStorage
    // would persist across browser restarts which is the wrong default
    // for an industrial-control SPA. Where the browser can, the app's
    // open tabs share it (tabSession below).
    userStore: new WebStorageStateStore({ store: tabStorage }),
    // Auto-renew the access_token a minute before expiry — driven by
    // the refresh_token from offline_access.
    automaticSilentRenew: true,
    // Reasonable defaults for monitoring sliding-session UX.
    monitorSession: false,
  };
}

/**
 * The sign-in shared across the app's open tabs (lib/tab-session.ts,
 * docs/decisions/2026-10-21_web-sign-in-shared-across-tabs.md): null
 * without OIDC, or where the browser lacks BroadcastChannel or Web Locks.
 */
export const tabSession: TabSession | null = isOidcConfigured()
  ? createBrowserTabSession()
  : null;

/**
 * The app's UserManager settings. With a tabSession, its store shares the
 * user with the other tabs and the tabSession renews the token, one tab at
 * a time; without one, oidc-client-ts renews it as before.
 */
export function appUserManagerSettings(
  base: UserManagerSettings,
  tab: TabSession | null,
): UserManagerSettings {
  return tab ? { ...base, userStore: tab.store, automaticSilentRenew: false } : base;
}

/**
 * Connects the tabSession to the UserManager made with its store. On the
 * OIDC callback of a new sign-in the tab is no longer signed out.
 */
export function connectTabSession(
  tab: TabSession,
  um: UserManager,
  isSigninCallback: boolean,
): void {
  tab.attach(um);
  if (isSigninCallback) tab.clearSignedOut();
}

/** The UserManager react-oidc-context uses. */
export const userManager: UserManager | null = isOidcConfigured()
  ? new UserManager(appUserManagerSettings(buildOidcConfig(), tabSession))
  : null;

if (tabSession && userManager) connectTabSession(tabSession, userManager, hasAuthParams());
